import React from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  RegisterReviewSection,
  RegisterReviewSectionTitle,
} from "../../../../Account/Register/Register.style";
import {
  AideCareAttestationsData,
  emptyLiveInAideState,
} from "../../liveInAideState";

// ---------------------------------------------------------------------------
// Screen 4 — HUD Live-In Aide care attestations (RESPROP LIA Questionnaire p2,
// "HUD Requirements – Definition of a Live-In Aide"). Three Yes/No questions,
// all required. Reuses the affordable Y/N ToggleButton pattern; booleans stored
// as boolean|null (`null` == unanswered). We capture the actual answer rather
// than force "Yes".
// ---------------------------------------------------------------------------

interface Props {
  /** Persisted care-attestations slice (state.careAttestations). */
  defaultValues?: Partial<AideCareAttestationsData>;
  onSubmit: (data: AideCareAttestationsData) => void | Promise<void>;
  isSaving?: boolean;
}

/** The three Y/N HUD attestation questions, in questionnaire order. */
const QUESTION_KEYS: (keyof AideCareAttestationsData)[] = [
  "essentialCare",
  "obligatedSupport",
  "wouldNotLiveExcept",
];

export const AideCareAttestations: React.FC<Props> = ({
  defaultValues,
  onSubmit,
  isSaving = false,
}) => {
  const { t } = useTranslation();

  const requiredMsg = t(
    "affordable.application.liveInAide.careAttestations.required"
  ) as string;

  const schema = yup.object({
    essentialCare: yup.boolean().nullable().required(requiredMsg),
    obligatedSupport: yup.boolean().nullable().required(requiredMsg),
    wouldNotLiveExcept: yup.boolean().nullable().required(requiredMsg),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AideCareAttestationsData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      essentialCare:
        defaultValues?.essentialCare ??
        emptyLiveInAideState.careAttestations.essentialCare,
      obligatedSupport:
        defaultValues?.obligatedSupport ??
        emptyLiveInAideState.careAttestations.obligatedSupport,
      wouldNotLiveExcept:
        defaultValues?.wouldNotLiveExcept ??
        emptyLiveInAideState.careAttestations.wouldNotLiveExcept,
    },
  });

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <RegisterReviewSection>
        <RegisterReviewSectionTitle>
          {t("affordable.application.liveInAide.careAttestations.title")}
        </RegisterReviewSectionTitle>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {t("affordable.application.liveInAide.careAttestations.subtitle")}
        </Typography>

        {QUESTION_KEYS.map((key) => (
          <Controller
            key={key}
            name={key}
            control={control}
            render={({ field }) => (
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }}>
                    {t(
                      `affordable.application.liveInAide.careAttestations.questions.${key}`
                    )}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={
                      field.value === true
                        ? "yes"
                        : field.value === false
                        ? "no"
                        : null
                    }
                    onChange={(_, val) => {
                      if (val !== null) field.onChange(val === "yes");
                    }}
                    sx={{ flexShrink: 0 }}
                  >
                    <ToggleButton value="yes" sx={{ px: 2 }}>
                      {t(
                        "affordable.application.liveInAide.careAttestations.yes"
                      )}
                    </ToggleButton>
                    <ToggleButton value="no" sx={{ px: 2 }}>
                      {t(
                        "affordable.application.liveInAide.careAttestations.no"
                      )}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {errors[key] && (
                  <FormHelperText error>
                    {errors[key]?.message as string}
                  </FormHelperText>
                )}
              </Box>
            )}
          />
        ))}
      </RegisterReviewSection>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3, mb: 4 }}>
        <Button
          variant="contained"
          type="submit"
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {t("account.leaseApplication.next")}
        </Button>
      </Box>
    </form>
  );
};
